import { Button, Input, Calendar, Select, Tabs } from "antd";
import { useState } from "react";
import { Check, Search, X } from "lucide-react";
import dayjs from "dayjs";
import { getOrderStatus, REPORT_STATUS } from "./reportStatus";

const PatientSection = ({
  patients,
  testOrders,
  selectedPatient,
  selectedTestOrder,
  onSelect,
  onSelectTestOrder,
  onPatientReload,
  comment,
  onCommentChange,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDate, setFilterDate] = useState(null);
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("patients");
  const [statusFilter, setStatusFilter] = useState("all");

  const patientLookup = (() => {
    const lookup = {
      byId: new Map(),
      byMobile: new Map(),
      byName: new Map(),
    };

    if (!Array.isArray(patients)) return lookup;

    patients.forEach((p) => {
      if (!p) return;
      if (p._id) lookup.byId.set(p._id.toString(), p);
      if (p.patientID) lookup.byId.set(p.patientID.toString(), p);
      if (p.mobile) lookup.byMobile.set(p.mobile.toString(), p);
      if (p.patientName) lookup.byName.set(p.patientName.toLowerCase(), p);
    });

    return lookup;
  })();

  const resolvePatientFromOrder = (order) => {
    if (!order) return null;
    if (order.patient && typeof order.patient === "object") return order.patient;
    if (!patients) return null;

    const patientId = order.patientId || order.patientID || order.patient?.patientID || order.patient?._id || order.patient;
    if (patientId) {
      const byId = patientLookup.byId.get(patientId.toString());
      if (byId) return byId;
    }

    const patientMobile = order.patient?.mobile || order.mobile;
    if (patientMobile) {
      const byMobile = patientLookup.byMobile.get(patientMobile.toString());
      if (byMobile) return byMobile;
    }

    const patientName = order.patient?.patientName || order.patientName;
    if (patientName) {
      const byName = patientLookup.byName.get(patientName.toLowerCase());
      if (byName) return byName;
    }

    return null;
  };

  const filterPatients = patients?.filter((p) => {
    const q = searchQuery.toLowerCase();

    const matchesPatient =
      p.patientName?.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q) ||
      (p.mobile && p.mobile.toString().includes(searchQuery)) ||
      p.patientID?.toString().includes(searchQuery) ||
      p.doctorName?.toLowerCase().includes(q) ||
      p.agentName?.toLowerCase().includes(q);

    // Also match if any of the patient's test orders match by order ID
    const matchesOrderId = Array.isArray(p.testOrders) && p.testOrders.some((o) =>
      o?.testOrderID?.toString().includes(searchQuery) ||
      o?._id?.toString().toLowerCase().includes(q)
    );

    const isSearchMatch = matchesPatient || matchesOrderId;

    const matchesStatus = statusFilter === "all"
      ? true
      : (p.testOrders || []).some((o) => getOrderStatus(o) === statusFilter);

    const searchByUpdatedOnDate = filterDate && (p.updatedAt && new Date(p.updatedAt).toISOString().slice(0, 10) === filterDate);
    const baseMatch = filterDate ? (isSearchMatch && searchByUpdatedOnDate) : isSearchMatch;

    return baseMatch && matchesStatus;
  });

  const finalList = (searchQuery || filterDate || statusFilter !== "all") ? filterPatients : patients;

  const filteredOrders = (testOrders || []).filter((order) => {
    const query = orderSearchQuery.toLowerCase();
    const resolvedPatient = resolvePatientFromOrder(order);
    const patientName = resolvedPatient?.patientName || order.patient?.patientName || order.patientName || "";
    const patientMobile = resolvedPatient?.mobile || order.patient?.mobile || order.mobile || "";
    const doctorName = order.doctor?.docName || order.doctorName || "";
    const agentName = order.agent?.name || order.agentName || "";
    const orderId = order.testOrderID || order.testOrderId || "";
    const externalDoctorName = order.externalDoctorName || "";

    const matchesSearch = (
      orderId.toString().toLowerCase().includes(query) ||
      patientName.toLowerCase().includes(query) ||
      patientMobile.toString().includes(query) ||
      doctorName.toLowerCase().includes(query) ||
      agentName.toLowerCase().includes(query) ||
      externalDoctorName.toLowerCase().includes(query)
    );

    const matchesStatus = statusFilter === "all" ? true : getOrderStatus(order) === statusFilter;
    const orderDateValue = order.createdAt || order.updatedAt || order.date;
    const matchesDate = filterDate
      ? (orderDateValue && new Date(orderDateValue).toISOString().slice(0, 10) === filterDate)
      : true;

    return matchesSearch && matchesStatus && matchesDate;
  });

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 p-2 max-h-[500px] overflow-hidden">
      <div className="flex justify-between items-center mb-2 ml-2 mt-2">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Search className="text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            Patient Selection
          </h2>
        </div>

        <div className="text-xs text-gray-400 mb-2 text-right italic" title={selectedPatient?.mobile || selectedTestOrder?.patient?.mobile || selectedTestOrder?.mobile || ""}>
          {selectedTestOrder ? (
            <>
              <span className="mr-2">Order: {selectedTestOrder?.testOrderID || selectedTestOrder?.testOrderId || "N/A"}</span>
              <span>{selectedPatient?.mobile || selectedTestOrder?.patient?.mobile || selectedTestOrder?.mobile || "N/A"}</span>
            </>
          ) : (
            selectedPatient?.mobile || "No Patient Selected"
          )}
          {selectedPatient && <X className="inline-block ml-2 cursor-pointer hover:text-red-600" onClick={() => onSelect?.(null)} />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 h-full p-2">
        <div className="lg:col-span-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
            <h3 className="font-semibold text-lg text-slate-700 mb-4 text-center">Filter by Date</h3>
            <Calendar
              fullscreen={false}
              className="w-full"
              value={filterDate ? dayjs(filterDate) : null}
              headerRender={({ value, onChange }) => {
                const current = value || dayjs();
                const currentYear = current.year();
                const years = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i);
                const months = Array.from({ length: 12 }, (_, i) => i);

                return (
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <Select
                      value={currentYear}
                      className="flex-1"
                      options={years.map((y) => ({ value: y, label: y }))}
                      onChange={(year) => onChange(current.year(year))}
                    />
                    <Select
                      value={current.month()}
                      className="flex-1"
                      options={months.map((m) => ({ value: m, label: dayjs().month(m).format("MMM") }))}
                      onChange={(month) => onChange(current.month(month))}
                    />
                  </div>
                );
              }}
              onSelect={(date) => {
                const selected = date.format("YYYY-MM-DD");
                setFilterDate(selected === filterDate ? null : selected);
              }}
            />
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div>
            <h3 className="font-semibold text-lg text-slate-700 mb-3">
              {activeTab === "orders" ? "Search Test Orders" : "Search Patients"}
            </h3>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <Input
                placeholder={activeTab === "orders" ? "Search by order id, patient name, mobile, doctor, agent" : "Search by name, email, mobile, doctor, or agent"}
                value={activeTab === "orders" ? orderSearchQuery : searchQuery}
                onChange={(e) => {
                  if (activeTab === "orders") setOrderSearchQuery(e.target.value);
                  else setSearchQuery(e.target.value);
                }}
                className="pl-5 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 hover:border-gray-300 transition-all duration-200"
              />
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg text-slate-700 mb-2">Filter By Status</h3>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-full rounded-xl border-0 border-gray-200 focus:border-blue-500 hover:border-gray-300 transition-all duration-200 bg-white"
              options={[
                { value: "all", label: activeTab === "orders" ? "All Orders" : "All Patients" },
                { value: REPORT_STATUS.PENDING, label: "Pending" },
                { value: REPORT_STATUS.COMPLETED, label: "Completed" },
              ]}
            />
          </div>

          <div>
            <h3 className="font-semibold text-lg text-slate-700 mb-3">Comments</h3>
            <Input.TextArea
              rows={4}
              autoSize={{ minRows: 6, maxRows: 7 }}
              value={comment || ""}
              onChange={(e) => onCommentChange?.(e.target.value)}
              aria-expanded={false}
              placeholder="Enter any comments regarding the report here..."
              className="rounded-xl border-2 border-gray-200 focus:border-blue-500 hover:border-gray-300 transition-all duration-200 overflow-y-auto"
            />
          </div>
        </div>

        <div className="lg:col-span-4 flex flex-col h-full">
          <Tabs
            defaultActiveKey="patients"
            onChange={setActiveTab}
            items={[
              {
                key: "patients",
                label: "Patients",
                children: (
                  <div className="bg-white border-2 border-t-0 border-gray-100 rounded-b-2xl flex-1 overflow-y-auto max-h-[370px]">
                    {(!finalList || finalList.length === 0) && (
                      <div className="p-8 text-center">
                        <div className="text-gray-400 mb-2">
                          <Search className="w-12 h-12 mx-auto" />
                        </div>
                        <h4 className="text-red-600 font-bold text-lg">No Records Found</h4>
                        {searchQuery ? <p className="text-gray-500 text-sm mt-1">Try adjusting your search criteria</p> : <Button value="link" onClick={onPatientReload}>Retry</Button>}
                      </div>
                    )}

                    {finalList?.map((p) => {
                      const selectedOrderId = selectedTestOrder?._id;
                      const selectedPatientId = selectedPatient?._id || selectedTestOrder?.patient?._id || selectedTestOrder?.patientId || selectedTestOrder?.patientID || selectedTestOrder?.patient;
                      const selectedPatientMobile = selectedPatient?.mobile || selectedTestOrder?.patient?.mobile || selectedTestOrder?.mobile;
                      const selected =
                        (selectedPatientId && p._id === selectedPatientId) ||
                        (selectedPatientMobile && p.mobile && p.mobile.toString() === selectedPatientMobile.toString()) ||
                        (selectedOrderId && p.testOrders?.some((to) => to?._id === selectedOrderId));

                      if (!p.testOrders || p.testOrders?.length === 0) return null;

                      return (
                        <div
                          key={p._id}
                          className={`group cursor-pointer transition-all duration-200 border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 ${selected ? "bg-gradient-to-r from-green-100 to-emerald-100 border-green-200" : ""}`}
                          onClick={() => onSelect(p)}
                        >
                          <div className="p-4">
                            <div className="grid grid-cols-12 items-center">
                              <div className="col-span-7 flex flex-col truncate">
                                <div className="font-semibold text-slate-800 group-hover:text-blue-700">
                                  {p.patientName}
                                  <span className="text-muted ml-2">({p.testOrders.length})</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1 truncate" title={p.mobile}>
                                  Mobile: {p.mobile || "N/A"}
                                </div>
                              </div>
                              <div className="col-span-4 flex flex-col ml-3">
                                <p className="px-3 py-1 text-xs font-semibold rounded-full">
                                  {p.testOrders.filter((o) => getOrderStatus(o) === REPORT_STATUS.COMPLETED).length} completed
                                </p>
                                <p className="px-3 py-1 text-xs font-semibold rounded-full">
                                  {p.testOrders.filter((o) => getOrderStatus(o) === REPORT_STATUS.PENDING).length} pending
                                </p>
                              </div>
                              <div className="col-span-1 flex justify-end">
                                {selected && (
                                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                    <Check className="w-4 h-4 text-white" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ),
              },
              {
                key: "orders",
                label: "Test Orders",
                children: (
                  <div className="bg-white border-2 border-t-0 border-gray-100 rounded-b-2xl flex-1 overflow-y-auto max-h-[370px]">
                    {(!filteredOrders || filteredOrders.length === 0) && (
                      <div className="p-8 text-center">
                        <div className="text-gray-400 mb-2">
                          <Search className="w-12 h-12 mx-auto" />
                        </div>
                        <h4 className="text-red-600 font-bold text-lg">No Test Orders Found</h4>
                        {orderSearchQuery && <p className="text-gray-500 text-sm mt-1">Try adjusting your search criteria</p>}
                      </div>
                    )}

                    {filteredOrders?.map((order) => {
                      const resolvedPatient = resolvePatientFromOrder(order);
                      const patientName = resolvedPatient?.patientName || order.patient?.patientName || order.patientName || "N/A";
                      const patientMobile = resolvedPatient?.mobile || order.patient?.mobile || order.mobile || "N/A";
                      const orderId = order.testOrderID || order.testOrderId || "N/A";
                      const selected = selectedTestOrder && order._id === selectedTestOrder._id;

                      return (
                        <div
                          key={order._id}
                          className={`group cursor-pointer transition-all duration-200 border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 ${selected ? "bg-gradient-to-r from-green-100 to-emerald-100 border-green-200" : ""}`}
                          onClick={() => onSelectTestOrder(order)}
                        >
                          <div className="p-4">
                            <div className="grid grid-cols-12 items-center">
                              <div className="col-span-8 flex flex-col truncate">
                                <div className="font-semibold text-slate-800 group-hover:text-blue-700">
                                  {patientName}
                                </div>
                                <div className="text-xs text-gray-500 mt-1 truncate">
                                  Order ID: {orderId}
                                </div>
                                <div className="text-xs text-gray-500 mt-1 truncate">
                                  Mobile: {patientMobile}
                                </div>
                              </div>
                              <div className="col-span-3 flex flex-col">
                                <p className="px-3 py-1 text-xs font-semibold rounded-full capitalize">
                                  {getOrderStatus(order)}
                                </p>
                              </div>
                              <div className="col-span-1 flex justify-end">
                                {selected && (
                                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                    <Check className="w-4 h-4 text-white" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default PatientSection;
